import { useState } from "react";
import classNames from "classnames/bind";
import { useDispatch, useSelector } from "react-redux";

import { IconClose } from "../../../assets/icons";

import { IState } from "../../../store";
import { setPopupNewCallType, setRefetch, useGetProjectColumnDescriptionsQuery } from "../../../store/services";
import {
  EEndpoints,
  EKeyCallType,
  ICallType,
  IColumn,
  accesses,
  standardColumns,
} from "../../../constants";
import { isEditableColumn } from "../../../types/TColumnDescription";

import styles from "./PopupNewCallType.module.scss";

const cx = classNames.bind(styles);

interface IPopupNewCallTypeProps {
  onClose: () => void;
}

const PopupNewCallType = ({ onClose }: IPopupNewCallTypeProps) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Form state
  const [newName, setNewName] = useState("");
  const [colsFiltered, setFiltered] = useState<number[]>([]);
  const [access, setAccess] = useState(accesses[0]);
  const [fromNumber, setFromNumber] = useState(-365);
  const [toNumber, setToNumber] = useState(0);
  const [selectedEditableFields, setSelectedEditableFields] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayColumnsSearchTerm, setDisplayColumnsSearchTerm] = useState("");

  const userEmail = useSelector((state: IState) => state.userReducer.email);
  const dispatch = useDispatch();
  const isRefetch = useSelector((state: IState) => state.refetchReducer.value);

  // Fetch column descriptions
  const { data: tableDescription, isLoading: isLoadingColumns } = useGetProjectColumnDescriptionsQuery();

  // Filter editable columns
  const editableColumns = tableDescription?.columns.filter(isEditableColumn) || [];
  
  // Function to check if a field should be hidden from editable selection
  const shouldHideEditableField = (fieldName: string): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    
    const hiddenPatterns = [
      '(t)',
      'timestamp',
      'ticket recipient',
      '(hours)',
      'old',
      'service cost',
      'last',
      'file',
      'link',
      'time',
      'response',
      'recipient',
      'nomad',
      'ignore'
    ];
    
    const exactHiddenNames = [
      'marsha',
      'city, country',
      'related site',
      'currency',
      'related contact',
      'call type'
    ];
    
    // Check if field name contains any of the hidden patterns
    if (hiddenPatterns.some(pattern => lowerFieldName.includes(pattern))) {
      return true;
    }
    
    // Check if field name exactly matches any of the exact hidden names
    if (exactHiddenNames.some(name => lowerFieldName === name)) {
      return true;
    }
    
    return false;
  };
  
  // Filter editable columns based on search term and exclude hidden fields
  const filteredEditableColumns = editableColumns
    .filter(column => !shouldHideEditableField(column.name))
    .filter(column => column.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleValue = (setState: (value: string) => void) => (e: any) => {
    if (e.target) setState((e.target as HTMLInputElement).value);
  };

  const handleSendData = async (newData: ICallType) => {
    try {
      const response = await fetch(
        EEndpoints.API_ENDPOINT + "/Project%20Note%20Type/create.json",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newData),
        }
      );
      return await response.json();
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = async () => {
    const newCallTypeColumns = standardColumns
      .filter((item) => !item.hiddenColumn)
      .map((el) => el.columnId)
      .filter((elem) => !colsFiltered.includes(elem));
    dispatch(setPopupNewCallType(false));
    const newData: ICallType = {
      [EKeyCallType.NAME]: newName,
      [EKeyCallType.CUSTOM_FILTERS]: JSON.stringify(newCallTypeColumns),
      [EKeyCallType.ACCESS]: access,
      [EKeyCallType.NOTE]: `Last 3 ${newName} Call Notes`,
      [EKeyCallType.USER]: userEmail,
      [EKeyCallType.DAYS]: JSON.stringify([fromNumber, toNumber]),
      [EKeyCallType.EDITABLE_FIELDS]: JSON.stringify(selectedEditableFields),
    };
    await handleSendData(newData);
    await dispatch(setRefetch(!isRefetch));
    handleReset();
  };

  const handleNumber = (setState: (value: number) => void) => (e: any) => {
    if (e.target) setState(Number((e.target as HTMLInputElement).value));
  };

  const handleReset = () => {
    setNewName("");
    setAccess(accesses[0]);
    setFromNumber(-365);
    setToNumber(0);
    setSelectedEditableFields([]);
    setSearchTerm("");
    setDisplayColumnsSearchTerm("");
    setCurrentStep(1);
  };

  const handleFilter = (id: number) => {
    if (colsFiltered.includes(id)) {
      setFiltered(colsFiltered.filter((el) => el !== id));
    } else setFiltered([...colsFiltered, id]);
  };

  const handleEditableFieldToggle = (columnId: number) => {
    if (selectedEditableFields.includes(columnId)) {
      setSelectedEditableFields(selectedEditableFields.filter(id => id !== columnId));
    } else {
      setSelectedEditableFields([...selectedEditableFields, columnId]);
    }
  };

  const columnsFiltered: IColumn[] = standardColumns.filter(
    (el: IColumn) => !el?.hiddenColumn
  );
  
  // Filter display columns based on search term
  const filteredDisplayColumns = columnsFiltered.filter(column =>
    column.name.toLowerCase().includes(displayColumnsSearchTerm.toLowerCase())
  );

  // Helper functions for display columns select all/deselect all
  const areAllDisplayColumnsSelected = () => {
    return filteredDisplayColumns.every(column => !colsFiltered.includes(column.columnId));
  };

  const handleDisplayColumnsSelectAll = () => {
    if (areAllDisplayColumnsSelected()) {
      // Deselect all filtered columns (add them to colsFiltered)
      const filteredColumnIds = filteredDisplayColumns.map(column => column.columnId);
      setFiltered([...colsFiltered, ...filteredColumnIds.filter(id => !colsFiltered.includes(id))]);
    } else {
      // Select all filtered columns (remove them from colsFiltered)
      const filteredColumnIds = filteredDisplayColumns.map(column => column.columnId);
      setFiltered(colsFiltered.filter(id => !filteredColumnIds.includes(id)));
    }
  };

  // Helper functions for editable fields select all/deselect all
  const areAllEditableFieldsSelected = () => {
    return filteredEditableColumns.every(column => selectedEditableFields.includes(column.id));
  };

  const handleEditableFieldsSelectAll = () => {
    if (areAllEditableFieldsSelected()) {
      // Deselect all filtered fields
      const filteredFieldIds = filteredEditableColumns.map(column => column.id);
      setSelectedEditableFields(selectedEditableFields.filter(id => !filteredFieldIds.includes(id)));
    } else {
      // Select all filtered fields
      const filteredFieldIds = filteredEditableColumns.map(column => column.id);
      setSelectedEditableFields([...selectedEditableFields, ...filteredFieldIds.filter(id => !selectedEditableFields.includes(id))]);
    }
  };

  // Step navigation
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Validation for each step
  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return newName.trim() !== "";
      case 2:
        return true; // Can always proceed from step 2
      case 3:
        return true; // Step 3 is the final step
      default:
        return false;
    }
  };

  // Step titles
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Basic Information";
      case 2:
        return "Display Columns";
      case 3:
        return "Editable Fields";
      default:
        return "New Call Type";
    }
  };

  // Render Step 1: Basic Information
  const renderStep1 = () => (
    <div>
      <div className={cx("section")}>
        <h5>Call Type Name</h5>
        <div className={cx("input-wrapper")}>
          <input
            value={newName}
            placeholder="Enter call type name"
            onChange={handleValue(setNewName)}
          />
        </div>
      </div>

      <div className={cx("access-days-wrapper")}>
        <div className={cx("section", "section-half")}>
          <h5>Access Level</h5>
          <p className={cx("field-description")}>
            Choose who can access this call type
          </p>
          <div className={cx("access-wrapper")}>
            {accesses.map((el) => (
              <div
                className={cx(access === el && "active")}
                key={el}
                onClick={() => setAccess(el)}
              >
                {el}
              </div>
            ))}
          </div>
        </div>

        <div className={cx("section", "section-half")}>
          <h5>Days to Opening</h5>
          <p className={cx("field-description")}>
            Set the date range for filtering notes (use negative numbers for past dates)
          </p>
          <div className={cx("daystoopening")}>
            <div className={cx("daystoopening-wrapper")}>
              <div className={cx("days-wrapper")}>
                <input
                  type="number"
                  min="-999"
                  max="999"
                  placeholder="From"
                  id="relative-date-from"
                  onChange={handleNumber(setFromNumber)}
                  value={fromNumber !== 0 ? fromNumber : ""}
                />
                <span>-</span>
                <input
                  type="number"
                  min="-999"
                  max="999"
                  placeholder="To"
                  id="relative-date-to"
                  onChange={handleNumber(setToNumber)}
                  value={toNumber !== 0 ? toNumber : ""}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 2: Display Columns
  const renderStep2 = () => (
    <div>
      <h5>Select columns to display in the table</h5>
      <div className={cx("search-section")}>
        <input
          type="text"
          placeholder="Search columns..."
          value={displayColumnsSearchTerm}
          onChange={(e) => setDisplayColumnsSearchTerm(e.target.value)}
          className={cx("search-input")}
        />
      </div>
      {filteredDisplayColumns.length > 0 && (
        <div className={cx("select-all-section")}>
          <button
            className={cx("select-all-button")}
            onClick={handleDisplayColumnsSelectAll}
          >
            {areAllDisplayColumnsSelected() ? "Deselect All" : "Select All"}
          </button>
        </div>
      )}
      <div className={cx("checkbox-wrapper", "display-columns-wrapper")}>
        {filteredDisplayColumns.length > 0 ? (
          filteredDisplayColumns.map((el) => (
            <div
              className={cx(
                "column-checkbox",
                "display-column",
                el && !colsFiltered.includes(el.columnId) && "checked"
              )}
              key={`column-selector-${el?.id}`}
              onClick={() => handleFilter(el?.columnId || 0)}
              title={el?.name}
            >
              <span className={cx("field-name")}>{el?.name}</span>
            </div>
          ))
        ) : (
          <div className={cx("no-fields")}>No columns found matching "{displayColumnsSearchTerm}"</div>
        )}
      </div>
    </div>
  );

  // Render Step 3: Editable Fields
  const renderStep3 = () => (
    <div>
      <h5>Select fields to be editable in the sidebar</h5>
      <div className={cx("editable-fields-section")}>
        {isLoadingColumns ? (
          <div className={cx("loading")}>Loading fields...</div>
        ) : editableColumns.length > 0 ? (
          <>
            <div className={cx("search-section")}>
              <input
                type="text"
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cx("search-input")}
              />
            </div>
            {filteredEditableColumns.length > 0 && (
              <div className={cx("select-all-section")}>
                <button
                  className={cx("select-all-button")}
                  onClick={handleEditableFieldsSelectAll}
                >
                  {areAllEditableFieldsSelected() ? "Deselect All" : "Select All"}
                </button>
              </div>
            )}
            <div className={cx("checkbox-wrapper", "editable-fields-wrapper")}>
              {filteredEditableColumns.length > 0 ? (
                filteredEditableColumns.map((column) => (
                  <div
                    key={`editable-${column.id}`}
                    className={cx(
                      "column-checkbox",
                      "editable-field",
                      selectedEditableFields.includes(column.id) && "checked"
                    )}
                    onClick={() => handleEditableFieldToggle(column.id)}
                    title={column.description || column.name}
                  >
                    <span className={cx("field-name")}>{column.name}</span>
                  </div>
                ))
              ) : (
                <div className={cx("no-fields")}>No fields found matching "{searchTerm}"</div>
              )}
            </div>
          </>
        ) : (
          <div className={cx("no-fields")}>No editable fields available</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cx("column-selector")}>
      <button className={cx("close")} onClick={onClose}>
        <IconClose size="16px"/>
      </button>
      
      <div className={cx("step-header")}>
        <h4>{getStepTitle()}</h4>
        <div className={cx("step-indicator")}>
          <span>Step {currentStep} of {totalSteps}</span>
        </div>
      </div>

      <div className={cx("step-content")}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      <div className={cx("step-navigation", { "single-button": currentStep === 1 })}>
        {currentStep > 1 && (
          <button 
            className={cx("navigation-button", "previous")} 
            onClick={handlePrevious}
          >
            Previous
          </button>
        )}
        
        {currentStep < totalSteps ? (
          <button 
            className={cx("navigation-button", "next")} 
            onClick={handleNext}
            disabled={!canProceedToNextStep()}
          >
            Next
          </button>
        ) : (
          <button 
            className={cx("navigation-button", "save")} 
            onClick={handleSave}
          >
            Save Call Type
          </button>
        )}
      </div>
    </div>
  );
};

export default PopupNewCallType;
